import dynamic from "next/dynamic";
import { useMemo, useState, useEffect } from "react";
import { useRouter } from "next/router";
import { usePathname, useSearchParams } from "next/navigation";
import PropTypes from "prop-types";
import { getPageShellDataSafely } from "@/services/contenful/getPageShellDataSafe";
import ProductListing from "@/components/ProductListing";
import {
  getFaucetLabelFromServer,
  getListFromServer,
} from "@/services/server/plp.server";
import { formatListingData } from "@/services/server/plpHelper";
import { getPlpProcessedData } from "@/services/server/plpResponseProcessor";
import Landing from "@/components/landing";
import { siteName } from "@/constants";
import NotFoundSection from "@/components/detailsComponent/NotFound";

const Loader = dynamic(() => import("@/components/loader"));

const LandingPage = ({
  pageData = {},
  locale = "en",
  plpData = {},
  facetLabelsState = {},
  categoryConfig = "",
  params = {},
  compareMappingsData = {},
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const hasInitialProducts = useMemo(
    () => Array.isArray(plpData?.data) && plpData.data.length > 0,
    [plpData]
  );
  const [showNotFound, setShowNotFound] = useState(!hasInitialProducts);

  useEffect(() => {
    setShowNotFound(!hasInitialProducts);
  }, [hasInitialProducts, pathname, searchParams]);

  if (router.isFallback) {
    return <Loader loading={router.isFallback} />;
  }

  return (
    <>
      {showNotFound ? (
        <NotFoundSection />
      ) : (
        <>
          <Landing pageData={pageData} />
          <ProductListing
            key={`${locale}-${pathname}-${searchParams.toString()}`}
            locale={locale}
            plpData={plpData}
            facetLabelsState={facetLabelsState}
            categoryConfig={categoryConfig}
            params={params}
            onNoResultsChange={setShowNotFound}
            compareMappingsData={compareMappingsData}
          />
        </>
      )}
    </>
  );
};

export async function getServerSideProps({
  req,
  params,
  preview,
  locale = "en",
}) {
  const { slug = "/" } = params;
  const defaultLocale = locale === "default" ? "en" : locale;

  const extractedSlug = Array.isArray(slug) ? slug.join("/") : slug;
  const {
    items,
    headerNavigationData,
    footerNavigationData,
    promoBannerData,
    worldwideMenuData,
    compareMappingsData,
  } = await getPageShellDataSafely({
    slug: `/browse/${extractedSlug}`,
    preview,
    contentType: "brazilLandingPage",
    fetchEntryBySlugFlag: true,
    locale,
  });

  const pageData = items?.[0]?.fields;
  if (!pageData) {
    return {
      notFound: true,
    };
  }

  const { pageSections = [] } = pageData ?? {};

  const foundObject = pageSections.find(
    item => item?.fields?.categoryConfig !== undefined
  );

  const categoryConfig = foundObject ? foundObject?.fields?.categoryConfig : "";

  // if serverside url has values in facets, reformat them
  let facetLabelsState = {};

  let plpData = {};

  try {
    const {
      fusion: {
        facet_labels: facetlabels = {},
        color_swatch_labels: colorSwatchLabels = {},
        category_labels = {},
      } = {},
      facet_counts: {
        facet_fields: facetFields = {},
        facet_ranges: facetRanges = {},
      } = {},
    } =
      (await getFaucetLabelFromServer(
        categoryConfig,
        siteName,
        defaultLocale
      )) || {};

    if (facetlabels) {
      facetLabelsState = {
        facetlabels,
        facetFields,
        colorSwatchLabels,
        facetRanges,
        category_labels,
      };

      const serverData = await getListFromServer(
        categoryConfig,
        siteName,
        defaultLocale,
        req
      );
      const formattedData = await formatListingData(serverData, false);
      plpData = await getPlpProcessedData(formattedData);
      const { data: plpListingData = [] } = plpData;
    }
  } catch (error) {
    plpData = {};
    console.error("Error reformatting facets:", error);
  }

  return {
    props: {
      pageData,
      headerNavigationData,
      footerNavigationData,
      promoBannerData,
      world: worldwideMenuData,
      compareMappingsData,
      locale,
      plpData,
      facetLabelsState,
      categoryConfig,
      params,
    },
  };
}

LandingPage.propTypes = {
  pageData: PropTypes.object,
  locale: PropTypes.string,
  plpData: PropTypes.object,
  facetLabelsState: PropTypes.object,
  categoryConfig: PropTypes.string,
  params: PropTypes.object,
};

export default LandingPage;
